<template lang="pug">
    .foreign-store
        i.el-icon-fa-store.fas.foreign-store__icon
        .foreign-store__title {{ title }}
        .foreign-store__text {{ text }}
        .foreign-store__actions
            el-button(type="primary" size="small" @click="switchStore")
                | {{ $t('Switch to this store') }}
            el-button(v-if="backRoute" size="small" @click="goBack") {{ backLabel }}
</template>

<i18n>
{
    "en": {
        "Order {record} was placed in {store}. Its documents, numbering and branding belong to that store, so it opens there.": "Order {record} was placed in {store}. Its documents, numbering and branding belong to that store, so it opens there.",
        "Cart {record} belongs to {store}. Carts follow the store their customer shops in, so it opens there.": "Cart {record} belongs to {store}. Carts follow the store their customer shops in, so it opens there.",
        "Wish list {record} belongs to {store}. Wish lists follow the store their customer shops in, so it opens there.": "Wish list {record} belongs to {store}. Wish lists follow the store their customer shops in, so it opens there.",
        "Ticket {record} belongs to the helpdesk of {store}, so it opens there.": "Ticket {record} belongs to the helpdesk of {store}, so it opens there."
    },
    "de": {
        "This order belongs to another store": "Diese Bestellung gehört zu einem anderen Store",
        "This cart belongs to another store": "Dieser Warenkorb gehört zu einem anderen Store",
        "This wish list belongs to another store": "Diese Wunschliste gehört zu einem anderen Store",
        "This ticket belongs to another website": "Dieses Ticket gehört zu einem anderen Webauftritt",
        "Ticket {record} belongs to the helpdesk of {store}, so it opens there.": "Ticket {record} gehört zum Helpdesk von {store}, deshalb wird es dort geöffnet.",
        "Back to the ticket list": "Zurück zur Ticketliste",
        "Order {record} was placed in {store}. Its documents, numbering and branding belong to that store, so it opens there.": "Bestellung {record} wurde in {store} aufgegeben. Dokumente, Nummernkreis und Branding gehören zu diesem Store, deshalb wird sie dort geöffnet.",
        "Cart {record} belongs to {store}. Carts follow the store their customer shops in, so it opens there.": "Warenkorb {record} gehört zu {store}. Warenkörbe folgen dem Store, in dem der Kunde einkauft, deshalb wird er dort geöffnet.",
        "Wish list {record} belongs to {store}. Wish lists follow the store their customer shops in, so it opens there.": "Wunschliste {record} gehört zu {store}. Wunschlisten folgen dem Store, in dem der Kunde einkauft, deshalb wird sie dort geöffnet.",
        "Switch to this store": "Zu diesem Store wechseln",
        "Back to the order list": "Zurück zur Bestellliste",
        "Back to the cart list": "Zurück zur Warenkorbliste",
        "Back to the wish list overview": "Zurück zur Wunschlistenübersicht"
    }
}
</i18n>

<script>
import { useSetupStore } from '@/stores'
import router from '@/router'

/**
 * A record of a shared shop belongs to ONE store, and the API refuses it from
 * another store's context. Saying which store that is - and offering the one click
 * that fixes it - beats an error code over an empty form.
 */
export default {
    name: 'ForeignStorePanel',
    props: {
        store: {
            type: Object,
            required: true,
        },
        recordId: {
            type: [String, Number],
            default: null,
        },
        recordType: {
            type: String,
            default: 'order',
            validator: value => ['order', 'cart', 'wishList', 'ticket'].includes(value),
        },
        backRoute: {
            type: String,
            default: null,
        },
    },
    computed: {
        title() {
            const titles = {
                order: 'This order belongs to another store',
                cart: 'This cart belongs to another store',
                wishList: 'This wish list belongs to another store',
                ticket: 'This ticket belongs to another website',
            };

            return this.$t(titles[this.recordType]);
        },
        text() {
            const texts = {
                order: 'Order {record} was placed in {store}. Its documents, numbering and branding belong to that store, so it opens there.',
                cart: 'Cart {record} belongs to {store}. Carts follow the store their customer shops in, so it opens there.',
                wishList: 'Wish list {record} belongs to {store}. Wish lists follow the store their customer shops in, so it opens there.',
                ticket: 'Ticket {record} belongs to the helpdesk of {store}, so it opens there.',
            };

            return this.$t(texts[this.recordType], { record: this.recordId, store: this.store.name });
        },
        backLabel() {
            const labels = {
                order: 'Back to the order list',
                cart: 'Back to the cart list',
                wishList: 'Back to the wish list overview',
                ticket: 'Back to the ticket list',
            };

            return this.$t(labels[this.recordType]);
        },
    },
    methods: {
        switchStore() {
            const target = (useSetupStore().cmss || []).find(cms => parseInt(cms.id) === parseInt(this.store.id));
            if (!target) return;

            useSetupStore().updateActiveCms(target);
            this.$emit('switched', target);
        },
        goBack() {
            router.push({ name: this.backRoute });
        },
    },
}
</script>

<style lang="sass" scoped>
// Self-contained on purpose: several host pages (Cart, WishList, Purchase) zero
// out .el-card__body padding for their own layout, so a shared panel that
// borrowed el-card would render flush against its border on those pages and
// correctly everywhere else - the kind of difference that only shows up on the
// page you did not open.
.foreign-store
    max-width: 520px
    margin: 60px auto
    padding: 28px 24px
    background: #fff
    border: 1px solid #ebeef5
    border-radius: 4px
    text-align: center

    &__icon
        font-size: 28px
        color: #E6A23C
        margin-bottom: 12px

    &__title
        font-size: 16px
        font-weight: 600
        margin-bottom: 8px

    &__text
        color: #606266
        font-size: 13px
        line-height: 1.5
        margin-bottom: 18px

    &__actions
        display: flex
        flex-wrap: wrap
        justify-content: center
        gap: 10px

        // Store names are free text and can be long; let a label wrap inside the
        // card instead of pushing the buttons out of it.
        .el-button
            max-width: 100%
            white-space: normal
            line-height: 1.3

        .el-button + .el-button
            margin-left: 0
</style>
